// ** React Imports
import { useState, useEffect, MouseEvent, useCallback, ReactElement } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import { DataGrid, GridEventListener } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography'

// ** Store Imports
import { SelectOption } from 'src/types/common';

// ** Custom Components Imports

import EditCustomerDrawer from 'src/features/customers/EditCustomerDrawer'
import _ from 'lodash'
import { IconButton } from '@mui/material'
import PencilOutline from 'mdi-material-ui/PencilOutline'
import { Organisation } from '../../../types/Organisation';
import RenderIf, { ClaimType, ClaimValue } from '../../common/RenderIf';
import RoleSelector from '../../roles/RoleSelector';
import TableHeader from './TableHeader';
import EditOrganisationDialog from './EditOrganisationDialog';
import { useGetItems } from '../../../hooks/organisations.hooks';
import PageHeader from '../../../materio/@core/components/page-header';

interface CellType {
    row: Organisation
}


const OrganisationList = () => {
    const [pageSize, setPageSize] = useState(10);
    const [pageNumber, setPageNumber] = useState(1);
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [editOpen, setEditOpen] = useState<boolean>(false)
    const [selectedRecord, setSelectedRecord] = useState(undefined as undefined | Organisation);
    const { data: pagedList, refetch } = useGetItems(pageNumber, pageSize, searchTerm);


    useEffect(() => {
        refetch();
    }, [searchTerm])

    const columns = [
        {
            flex: 0.2,
            minWidth: 230,
            field: 'name',
            headerName: 'Organization',
            renderCell: ({ row }: CellType) => {
                const { id, name } = row

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component='a'
                                variant='body2'
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {name}
                            </Typography>
                        </Box>
                    </Box>
                )
            }
        },
        {
            flex: 0.1,
            minWidth: 100,
            field: 'selectedPlan',
            headerName: 'Selected Plan',
            renderCell: ({ row }: CellType) => {


                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component='a'
                                variant='body2'
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {row.selectedPlan}
                            </Typography>
                        </Box>
                    </Box>
                )
            }
        },
        {
            flex: 0.1,
            minWidth: 100,
            field: 'planType',
            headerName: 'Plan Type',
            renderCell: ({ row }: CellType) => {


                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component='a'
                                variant='body2'
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {row.planType}
                            </Typography>
                        </Box>
                    </Box>
                )
            }
        },
        {
            flex: 0.1,
            minWidth: 100,
            field: 'annualMonthlyPriceInUsd',
            headerName: 'Monthly Price (USD)',
            renderCell: ({ row }: CellType) => {


                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component='a'
                                variant='body2'
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {row.planType == 'monthly' ? row.monthlyPriceInUsd : row.annualMonthlyPriceInUsd}
                            </Typography>
                        </Box>
                    </Box>
                )
            }
        },
        {
            flex: 0.1,
            minWidth: 100,
            field: 'annualPriceInUsd',
            headerName: 'Annual Price (USD)',
            renderCell: ({ row }: CellType) => {


                return (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column' }}>
                            <Typography
                                noWrap
                                component='a'
                                variant='body2'
                                sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none' }}
                            >
                                {row.annualPriceInUsd}
                            </Typography>
                        </Box>
                    </Box>
                )
            }
        },
        {
            flex: 0.1,
            field: '',
            maxWidth: 150,
            headerName: 'Actions',
            renderCell: ({ row }: CellType) => {
                return (
                    <>
                        <RenderIf claimType={ClaimType.Organisations} claimValue={ClaimValue.Edit}>
                            <IconButton onClick={() => onRowClick(row)} title="Edit">
                                <PencilOutline fontSize='small' />
                            </IconButton>
                        </RenderIf>

                        <RoleSelector claimType={ClaimType.Organisations} recordId={row.id} />
                    </>
                )
            }
        }
    ]

    const handleFilter = useCallback((val: string) => {
        setSearchTerm(val)
    }, [])

    const toggleEditDrawer = async () => {
        //if (editOpen) {
        //    await loadRecords(1, pageSize);
        //}
        setEditOpen(!editOpen)
        setSelectedRecord(undefined);
    }

    const onRowClick = (row: Organisation) => {
        setSelectedRecord(row);
        setEditOpen(true);
    };

    if (!pagedList) return null;

    return (
        <Grid container spacing={6}>
            <Grid item xs={12}>
                <PageHeader
                    title={<Typography variant='h5'>Organization List</Typography>}
                    subtitle={
                        <Typography variant='body2'>
                            Create and manage organizations.
                        </Typography>
                    }
                />
            </Grid>
            <Grid item xs={12}>                
                <Card>
                    <TableHeader value={searchTerm} handleFilter={handleFilter} />
                    <DataGrid
                        autoHeight
                        pagination
                        rows={pagedList.items}
                        columns={columns}
                        pageSize={pageSize}
                        disableSelectionOnClick
                        rowsPerPageOptions={[10, 25, 50]}
                        onPageSizeChange={(newPageSize: number) => setPageSize(newPageSize)}
                    />
                </Card>
            </Grid>

            {selectedRecord && <EditOrganisationDialog open={editOpen} onClose={toggleEditDrawer} record={selectedRecord} key={selectedRecord.id} />}
        </Grid>
    )
}

export { OrganisationList }
export default OrganisationList
